<lib-auth-layout>
  <div *transloco="let t">
    <article class="auth-card auth-card--register">
      <header class="auth-card__header">
        <img
          src="/assets/logo.png"
          [alt]="t('brand.logo_alt')"
          class="auth-card__logo"
        />
        <h2>{{ t('register.header') }}</h2>
      </header>

      <div class="auth-card__body">
        <form [formGroup]="registerForm" (ngSubmit)="onSubmit()" novalidate>
          @if (errorMessage()) {
          <div class="alert alert-danger" role="alert">
            {{ errorMessage() }}
          </div>
          }

          <div class="auth-card__form-shell">
            <div class="auth-input-wrap">
              <div class="auth-input-row">
                <span class="auth-input-row__icon">
                  <i class="far fa-user"></i>
                </span>
                <input
                  id="name"
                  type="text"
                  class="form-control auth-input"
                  [class.is-invalid]="name?.invalid && name?.touched"
                  formControlName="name"
                  placeholder="{{ t('register.name_label') }}"
                  autocomplete="name"
                />
              </div>
              @if (name?.invalid && name?.touched) {
              <div class="invalid-feedback d-block">
                @if (name?.errors?.['required']) {
                {{ t('validation.name_required') }}
                } @if (name?.errors?.['minlength']) {
                {{ t('validation.name_min') }}
                }
              </div>
              } @if (fieldErrors()['name']) {
              <div class="invalid-feedback d-block">
                {{ fieldErrors()['name'] }}
              </div>
              }
            </div>

            <div class="auth-input-wrap">
              <div class="auth-input-row">
                <span class="auth-input-row__icon">
                  <i class="far fa-envelope"></i>
                </span>
                <input
                  id="email"
                  type="email"
                  class="form-control auth-input"
                  [class.is-invalid]="email?.invalid && email?.touched"
                  formControlName="email"
                  placeholder="{{ t('register.email_label') }}"
                  autocomplete="email"
                />
              </div>
              @if (email?.invalid && email?.touched) {
              <div class="invalid-feedback d-block">
                @if (email?.errors?.['required']) {
                {{ t('validation.email_required') }}
                } @if (email?.errors?.['email']) {
                {{ t('validation.email_invalid') }}
                }
              </div>
              } @if (fieldErrors()['email']) {
              <div class="invalid-feedback d-block">
                {{ fieldErrors()['email'] }}
              </div>
              }
            </div>

            <div class="auth-input-wrap">
              <div class="auth-input-row auth-input-row--password">
                <span class="auth-input-row__icon">
                  <i class="fas fa-lock"></i>
                </span>
                <input
                  id="password"
                  [type]="showPassword() ? 'text' : 'password'"
                  class="form-control auth-input"
                  [class.is-invalid]="password?.invalid && password?.touched"
                  formControlName="password"
                  placeholder="{{ t('register.password_label') }}"
                  autocomplete="new-password"
                  (focus)="passwordFieldActive.set(true)"
                  (blur)="passwordFieldActive.set(false)"
                />
                <button
                  type="button"
                  class="auth-input-row__action"
                  (click)="togglePasswordVisibility('password')"
                  [attr.aria-label]="t('register.password_label')"
                >
                  <i
                    [class]="showPassword() ? 'fas fa-eye-slash' : 'fas fa-eye'"
                  ></i>
                </button>
              </div>
              @if (password?.invalid && password?.touched) {
              <div class="invalid-feedback d-block">
                @if (password?.errors?.['required']) {
                {{ t('validation.password_required') }}
                }
              </div>
              } @if (password?.value || passwordFieldActive() ||
              password?.touched) {
              <div class="password-requirements">
                <ul>
                  <li [class.met]="passwordRequirements().hasMinLength">
                    <i
                      [class]="
                        passwordRequirements().hasMinLength
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('register.password_requirements.min') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasUppercase">
                    <i
                      [class]="
                        passwordRequirements().hasUppercase
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('register.password_requirements.upper') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasLowercase">
                    <i
                      [class]="
                        passwordRequirements().hasLowercase
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('register.password_requirements.lower') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasNumber">
                    <i
                      [class]="
                        passwordRequirements().hasNumber
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('register.password_requirements.number') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasSpecialChar">
                    <i
                      [class]="
                        passwordRequirements().hasSpecialChar
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('register.password_requirements.special') }}
                  </li>
                </ul>
              </div>
              }
            </div>

            <div class="auth-input-wrap">
              <div class="auth-input-row auth-input-row--password">
                <span class="auth-input-row__icon">
                  <i class="fas fa-lock"></i>
                </span>
                <input
                  id="confirmPassword"
                  [type]="showConfirmPassword() ? 'text' : 'password'"
                  class="form-control auth-input"
                  [class.is-invalid]="
                    (confirmPassword?.invalid && confirmPassword?.touched) ||
                    (confirmPassword?.touched && !passwordsMatch)
                  "
                  formControlName="confirmPassword"
                  placeholder="{{ t('register.confirm_password_label') }}"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="auth-input-row__action"
                  (click)="togglePasswordVisibility('confirmPassword')"
                  [attr.aria-label]="t('register.confirm_password_label')"
                >
                  <i
                    [class]="
                      showConfirmPassword() ? 'fas fa-eye-slash' : 'fas fa-eye'
                    "
                  ></i>
                </button>
              </div>
              @if (confirmPassword?.touched && !passwordsMatch) {
              <div class="invalid-feedback d-block">
                {{ t('register.passwords_mismatch') }}
              </div>
              }
            </div>

            <div class="auth-remember-row auth-remember-row--terms">
              <div class="form-check auth-check auth-check--terms">
                <input
                  type="checkbox"
                  class="form-check-input"
                  [class.is-invalid]="
                    termsAgreed?.invalid && termsAgreed?.touched
                  "
                  id="agreed_to_terms"
                  formControlName="agreed_to_terms"
                />
                <label class="form-check-label" for="agreed_to_terms">
                  {{ t('register.terms_label') }}
                  <a routerLink="/coming-soon/terms-of-service">
                    {{ t('register.terms_of_service') }}
                  </a>
                  {{ t('register.terms_and') }}
                  <a routerLink="/coming-soon/privacy-policy">
                    {{ t('register.privacy_policy') }}
                  </a>
                </label>
              </div>
            </div>
            @if (termsAgreed?.invalid && termsAgreed?.touched) {
            <div class="invalid-feedback d-block auth-terms-error">
              {{ t('register.terms_required') }}
            </div>
            }

            <button
              type="submit"
              class="btn btn-primary btn-register auth-submit w-100"
              [disabled]="isLoading()"
            >
              @if (isLoading()) {
              <span
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t('register.button') }}
              } @else {
              <i class="fas fa-user-plus me-2"></i>
              {{ t('register.button') }}
              }
            </button>

            <div class="divider">
              <span class="divider-text">{{ t('common.or') }}</span>
            </div>

            <div class="social-login">
              <button
                type="button"
                class="social-btn google"
                [attr.aria-label]="t('register.social_google_aria_label')"
                (click)="onSocialRegister('Google')"
              >
                <i class="fab fa-google"></i>
              </button>
              <button
                type="button"
                class="social-btn facebook"
                [attr.aria-label]="t('register.social_facebook_aria_label')"
                (click)="onSocialRegister('Facebook')"
              >
                <i class="fab fa-facebook-f"></i>
              </button>
              <button
                type="button"
                class="social-btn apple"
                [attr.aria-label]="t('register.social_apple_aria_label')"
                (click)="onSocialRegister('Apple')"
              >
                <i class="fab fa-apple"></i>
              </button>
            </div>

            <p class="auth-alt-action text-center">
              {{ t('register.login_prompt') }}
              <a routerLink="/auth/login">{{ t('register.login_link') }}</a>
            </p>
          </div>
        </form>
      </div>
    </article>
  </div>
</lib-auth-layout>
